/**
 * Experience Creation Wizard - Main Container
 *
 * 5-stage wizard for creating new outdoor guide experiences.
 * Orchestrates wizard state, auto-save, navigation, and stage components.
 *
 * Epic: MVP 1.27 - Experience Creation Wizard
 * Stories: SCRUM-186 (Save & Exit), SCRUM-187 (Validation Framework)
 * Location: apps/web/src/components/experiences/creation/ExperienceCreationWizard.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WizardProgress from './WizardProgress';
import useExperienceWizard from '../../../hooks/experiences/useExperienceWizard';
import useExperienceAutoSave from '../../../hooks/experiences/useExperienceAutoSave';
import apiRequest from '../../../services/api';

// Stage components
import Stage1Basics from './stages/Stage1Basics';
import Stage2Logistics from './stages/Stage2Logistics';
import Stage3Pricing from './stages/Stage3Pricing';
import Stage4Media from './stages/Stage4Media';
import Stage5Review from './stages/Stage5Review';

/**
 * ExperienceCreationWizard Component
 *
 * @param {Object} props
 * @param {boolean} props.isNewExperience - Whether creating new or editing existing
 * @param {Object} props.initialData - Pre-populate wizard (for editing)
 * @returns {JSX.Element}
 */
export default function ExperienceCreationWizard({ isNewExperience = true, initialData = null }) {
  const navigate = useNavigate();
  const { experienceId } = useParams();

  // Modals
  const [showExitModal, setShowExitModal] = useState(false);
  const [showDraftRecoveryModal, setShowDraftRecoveryModal] = useState(false);
  const [recoveredDraft, setRecoveredDraft] = useState(null);

  // SCRUM-823: Publish state
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishErrors, setPublishErrors] = useState([]);

  // Wizard state management
  const {
    currentStage,
    completedStages,
    highestCompletedStage,
    formData,
    completionPercentage: _completionPercentage,
    goToStage,
    goToNextStage: _goToNextStage,
    goToPreviousStage,
    completeCurrentStage,
    canNavigateToStage: _canNavigateToStage,
    updateFormData: _updateFormData,
    updateField,
    getStageValidation,
    isCurrentStageValid,
    resetWizard
  } = useExperienceWizard({
    initialData: initialData || recoveredDraft?.formData,
    onStageChange: (newStage, oldStage) => {
      console.log(`Stage changed from ${oldStage} to ${newStage}`);
      // TODO: Track analytics event
    },
    onComplete: handleWizardComplete
  });

  // Auto-save with dual-tier storage
  const {
    isSaving,
    hasSaved: _hasSaved,
    hasError: _hasError,
    error: _saveError,
    saveStatusDisplay,
    saveDraftNow,
    getMostRecentDraft,
    clearLocalDraft
  } = useExperienceAutoSave(formData, experienceId, true);

  /**
   * Check for existing drafts on mount
   */
  useEffect(() => {
    async function checkForDrafts() {
      const draft = await getMostRecentDraft();
      if (draft && draft.formData) {
        setRecoveredDraft(draft);
        setShowDraftRecoveryModal(true);
      }
    }

    if (isNewExperience) {
      checkForDrafts();
    }
  }, [isNewExperience, getMostRecentDraft]);

  /**
   * Browser navigation protection (warn if unsaved changes)
   */
  useEffect(() => {
    const handleBeforeUnload = (  _e) => {
      // Only warn if there are unsaved changes and we're not saving
      if (formData && Object.keys(formData).length > 0 && !isSaving) {
        _e.preventDefault();
        _e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return _e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formData, isSaving]);

  /**
   * Handle wizard completion (Stage 5 publish)
   * SCRUM-823: Wire API call + display validation errors
   */
  async function handleWizardComplete(finalFormData) {
    setIsPublishing(true);
    setPublishErrors([]);

    try {
      if (!isNewExperience && experienceId) {
        // Edit mode: PUT to save changes to existing experience
        await apiRequest(`/api/v1/experiences/${experienceId}`, {
          method: 'PUT',
          body: JSON.stringify(finalFormData)
        });

        // Navigate back to guide's experiences list
        navigate('/guide/experiences');
      } else if (experienceId) {
        // Create mode with auto-saved draft — publish it
        await apiRequest(`/api/v1/experiences/${experienceId}/publish`, {
          method: 'POST'
        });

        clearLocalDraft();
        navigate(`/experience/${experienceId}`);
      } else {
        // Create mode, no auto-save — create + publish in one shot
        await apiRequest('/api/v1/experiences', {
          method: 'POST',
          body: JSON.stringify({ ...finalFormData, active: true })
        });

        clearLocalDraft();
        navigate('/experience/new');
      }
    } catch (err) {
      // Display validation errors from backend
      if (err.details && Array.isArray(err.details)) {
        setPublishErrors(err.details);
      } else {
        setPublishErrors([err.message || 'An unexpected error occurred. Please try again.']);
      }
    } finally {
      setIsPublishing(false);
    }
  }

  /**
   * Handle stage navigation click
   */
  const handleStageClick = useCallback((stageNumber) => {
    goToStage(stageNumber);
  }, [goToStage]);

  /**
   * Handle "Save Draft & Exit" button
   */
  const handleSaveDraftAndExit = async () => {
    const result = await saveDraftNow();
    if (result) {
      clearLocalDraft();
      navigate(isNewExperience ? '/guide/dashboard' : '/guide/experiences');
    } else {
      // TODO: Show error message
      console.error('Failed to save draft');
    }
  };

  /**
   * Handle "Exit" button (with confirmation if unsaved changes)
   */
  const exitDestination = isNewExperience ? '/guide/dashboard' : '/guide/experiences';

  const handleExit = () => {
    if (formData && Object.keys(formData).length > 0) {
      setShowExitModal(true);
    } else {
      navigate(exitDestination);
    }
  };

  /**
   * Handle draft recovery modal actions
   */
  const handleRecoverDraft = () => {
    if (recoveredDraft && recoveredDraft.formData) {
      _updateFormData(recoveredDraft.formData);
    }
    setShowDraftRecoveryModal(false);
  };

  const handleStartFresh = () => {
    clearLocalDraft();
    resetWizard();
    setShowDraftRecoveryModal(false);
  };

  /**
   * Render stage component based on currentStage
   */
  const renderStage = () => {
    const stageProps = {
      formData,
      updateField,
      updateFormData: _updateFormData,
      onNext: completeCurrentStage,
      onBack: goToPreviousStage,
      validation: getStageValidation(currentStage),
      isValid: isCurrentStageValid()
    };

    switch (currentStage) {
      case 1:
        return <Stage1Basics {...stageProps} />;
      case 2:
        return <Stage2Logistics {...stageProps} />;
      case 3:
        return <Stage3Pricing {...stageProps} />;
      case 4:
        return <Stage4Media {...stageProps} experienceId={experienceId} />;
      case 5:
        return <Stage5Review {...stageProps} experienceId={experienceId} isPublishing={isPublishing} isNewExperience={isNewExperience} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - SCRUM-349: Simplified with clear exit option */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {isNewExperience ? 'Create New Experience' : 'Edit Experience'}
              </h1>
              {saveStatusDisplay.text && (
                <p className={`text-sm ${saveStatusDisplay.color}`}>
                  {saveStatusDisplay.text}
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveDraftAndExit}
                disabled={isSaving}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isSaving ? 'Saving...' : 'Save Draft & Exit'}
              </button>
              <button
                onClick={handleExit}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Exit wizard"
                title="Exit wizard"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Indicator */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <WizardProgress
            currentStage={currentStage}
            completedStages={completedStages}
            highestCompletedStage={highestCompletedStage}
            onStageClick={handleStageClick}
          />
        </div>
      </div>

      {/* Stage Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* SCRUM-823: Publish validation error display */}
        {publishErrors.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-red-800 mb-2">
              Please fix the following before publishing:
            </h3>
            <ul className="list-disc list-inside space-y-1">
              {publishErrors.map((error, index) => (
                <li key={index} className="text-sm text-red-700">{error}</li>
              ))}
            </ul>
          </div>
        )}
        {renderStage()}
      </main>

      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <ExitConfirmationModal
          onSaveAndExit={handleSaveDraftAndExit}
          onDiscardAndExit={() => {
            clearLocalDraft();
            navigate(exitDestination);
          }}
          onCancel={() => setShowExitModal(false)}
        />
      )}

      {/* Draft Recovery Modal */}
      {showDraftRecoveryModal && (
        <DraftRecoveryModal
          draft={recoveredDraft}
          onRecover={handleRecoverDraft}
          onStartFresh={handleStartFresh}
        />
      )}
    </div>
  );
}

/**
 * Stage Placeholder Component (temporary until stages are created)
 */
function _StagePlaceholder({ stageNumber, title, formData, onNext, onBack, isValid }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        Stage {stageNumber}: {title}
      </h2>
      <p className="text-gray-600 mb-6">
        Stage component will be implemented here. Current form data:
      </p>
      <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-auto mb-6">
        {JSON.stringify(formData, null, 2)}
      </pre>
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        {stageNumber > 1 && (
          <button
            onClick={onBack}
            className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
          >
            Back
          </button>
        )}
        <button
          onClick={onNext}
          disabled={!isValid}
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold ml-auto"
        >
          {stageNumber === 5 ? 'Publish Experience' : 'Continue'}
        </button>
      </div>
    </div>
  );
}

/**
 * Exit Confirmation Modal
 */
function ExitConfirmationModal({ onSaveAndExit, onDiscardAndExit, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Save your progress?</h3>
        <p className="text-gray-600 mb-6">
          You have unsaved changes. What would you like to do?
        </p>
        <div className="space-y-3">
          <button
            onClick={onSaveAndExit}
            className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
          >
            Save Draft & Exit
          </button>
          <button
            onClick={onDiscardAndExit}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Discard Changes & Exit
          </button>
          <button
            onClick={onCancel}
            className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Draft Recovery Modal
 */
function DraftRecoveryModal({ draft, onRecover, onStartFresh }) {
  const savedDate = draft?.savedAt ? new Date(draft.savedAt) : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Continue where you left off?</h3>
        <p className="text-gray-600 mb-2">
          We found a draft from{' '}
          {savedDate?.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Would you like to continue editing or start fresh?
        </p>
        <div className="space-y-3">
          <button
            onClick={onRecover}
            className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
          >
            Continue Editing
          </button>
          <button
            onClick={onStartFresh}
            className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
          >
            Start Fresh
          </button>
        </div>
      </div>
    </div>
  );
}
